import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { assertPublicRateLimit } from "@/lib/rate-limit.server";
import { runTwinCheckAnalysis, type TwinCheckReport } from "@/lib/twin-check.server";

function hostSlug(host: string): string {
  return host
    .replace(/^www\./i, "")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 40);
}

// Run the audit and store it so it gets a shareable slug. Never throws for the
// caller: returns { ok:false } when the site is unreachable.
export const runTwinCheck = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ url: z.string().trim().min(3).max(300) }).parse(d))
  .handler(async ({ data }) => {
    await assertPublicRateLimit("twin-check", 6);
    const report = await runTwinCheckAnalysis(data.url);
    if (!report) return { ok: false as const };

    let slug: string | null = null;
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      slug = `${hostSlug(report.hostname)}-${Math.random().toString(36).slice(2, 6)}`;
      await (supabaseAdmin as any).from("twin_checks").insert({
        slug,
        url: report.url,
        hostname: report.hostname,
        score: report.score,
        report,
      });
    } catch {
      slug = null; // table may not exist yet — the report still shows inline
    }
    return { ok: true as const, slug, report };
  });

// Live demo Twin: answer a visitor's question grounded ONLY on the target
// company's website — the "build a twin from your URL and ask it" aha-moment,
// no account. Ephemeral: nothing is stored, the site content is cached briefly.
export const askDemoTwin = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        url: z.string().trim().min(3).max(300),
        question: z.string().trim().min(2).max(500),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("demo-twin", 12);
    const { fetchSiteContext } = await import("@/lib/twin-check.server");
    const ctx = await fetchSiteContext(data.url);
    if (!ctx || ctx.text.length < 80) return { ok: false as const, answer: null };
    const key = process.env.LOVABLE_API_KEY;
    if (!key) return { ok: false as const, answer: null };
    try {
      const { generateText } = await import("ai");
      const { createLovableAiGatewayProvider } = await import("@/lib/ai-gateway.server");
      const { FAST_MODEL } = await import("@/lib/ai-models");
      const gateway = createLovableAiGatewayProvider(key);
      const { text } = await generateText({
        model: gateway(FAST_MODEL),
        system:
          `You are the AI Twin of the company at ${ctx.hostname}, speaking in the first person ("we", "our"). ` +
          `Answer the visitor's question using ONLY the website content below. If the content doesn't cover it, ` +
          `say you don't have that detail yet and offer to have the team follow up. Be concise, warm and concrete. ` +
          `Never invent facts, prices or claims.\n` +
          // The demo bubble is a small chat surface: markdown bullet lists come
          // back as one long line of asterisks there, so ask for plain prose.
          `Write in plain prose — 2 to 4 short sentences, no markdown, no bullet points, no asterisks, no headings. ` +
          `If you must enumerate, write it as a sentence separated by semicolons.\n\n` +
          `=== WEBSITE CONTENT (${ctx.hostname}) ===\n` +
          `Title: ${ctx.title ?? ""}\n${ctx.description ?? ""}\n${ctx.text}`,
        prompt: data.question,
        maxOutputTokens: 400,
      });
      const answer = text.trim();
      return answer ? { ok: true as const, answer } : { ok: false as const, answer: null };
    } catch {
      return { ok: false as const, answer: null };
    }
  });

// Load a previously-stored report for the shareable /twin-check?r=<slug> page.
export const getTwinCheck = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ slug: z.string().trim().min(1).max(90) }).parse(d))
  .handler(async ({ data }) => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: row } = await (supabaseAdmin as any)
        .from("twin_checks")
        .select("report")
        .eq("slug", data.slug)
        .maybeSingle();
      if (row?.report) return { ok: true as const, report: row.report as TwinCheckReport };
    } catch {
      /* ignore */
    }
    return { ok: false as const };
  });
